'use client';

import { Fragment, memo, Suspense, useEffect, useRef, useState } from 'react';
import { IconCircleCheckFilled, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useSearchParams } from 'next/navigation';
import { CarSelectionForm, VehicleType } from '@/components/ui/LandingPageForm/CarSelectionForm';
import { RadioButton } from '@/components/ui/RadioButton/RadioButton';
import { Select } from '@/components/ui/Select/Select';
import { trackCtaClick } from '@/utils/tracking';
import { useExperiments } from '@/components/ExperimentsContext';
import { getExperimentBucket, REMOVE_DURATION_SELECTOR_FLAG_KEY } from '@/domain/experiments';
import styles from './LandingPageForm.module.css';
import Card from '@tempcover/bolt-ui/card';

export const LANDING_PAGE_FORM_TEST_ID = 'landing-page-form';

type DayInputMode = 'radio' | 'dropdown';

const DAY_INPUT_MODE_RADIO: DayInputMode = 'radio';
const DAY_INPUT_MODE_DROPDOWN: DayInputMode = 'dropdown';

export const FIXED_DURATION_ONE_DAY = '1 day';
export const FIXED_DURATION_TWO_DAYS = '2 days';
export const FIXED_DURATION_ONE_WEEK = '1 week';

export type JourneyType = 'none' | 'learner' | 'expat' | 'impounded' | 'courier';

interface LandingPageFormProps {
  heading?: string;
  subHeading?: string;
  regLabel: string;
  showRegLabel?: boolean;
  showRegToggle?: boolean;
  defaultDurationType?: string;
  daysOnly?: boolean;
  useBanner?: boolean;
  bannerCoverType?: string;
  bannerCoverDuration?: string;
  checkboxLabel?: string;
  useCardStyle?: boolean;
  formIntroText?: string;
  submitButtonText?: string;
  submitButtonColor?: 'orange' | 'purple';
  journey: JourneyType;
  regPlateOnly: boolean;
  showDurationPicker?: boolean;
  // Code-driven only — not exposed via LandingPageForm.registry.ts. Defaults to true
  // (own-brand) for TCEX-2988's duration-removal rollout; the affiliate/co-brand
  // PartnershipFormPanel block explicitly opts out by passing false.
  enableDurationRemoval?: boolean;
}

const LandingPageFormInner = ({
  heading,
  subHeading,
  regLabel,
  showRegLabel = true,
  showRegToggle = true,
  defaultDurationType = FIXED_DURATION_TWO_DAYS,
  daysOnly = false,
  useBanner = false,
  bannerCoverType,
  bannerCoverDuration,
  checkboxLabel,
  useCardStyle = false,
  formIntroText = 'To get started simply enter your reg...',
  submitButtonText = 'Continue',
  submitButtonColor = 'orange',
  journey,
  regPlateOnly,
  showDurationPicker = true,
  enableDurationRemoval = true,
}: LandingPageFormProps) => {
  const { experiments, hasAffiliateRef } = useExperiments();
  const durationRemovalBucket = getExperimentBucket(experiments, REMOVE_DURATION_SELECTOR_FLAG_KEY);
  const hideDuration =
    enableDurationRemoval && durationRemovalBucket === 'variant' && !hasAffiliateRef && journey !== 'impounded';

  const [durationType, setDurationType] = useState('');
  const [dayInputMode, setDayInputMode] = useState<DayInputMode>(DAY_INPUT_MODE_RADIO);
  const [accepted, setAccepted] = useState(false);
  const [showError, setShowError] = useState(false);

  const [reg, setReg] = useState<string>('');
  const [type, setType] = useState<VehicleType | ''>('');

  const Wrapper = useCardStyle ? Card : Fragment;
  const wrapperProps = useCardStyle ? { className: 'md:mx-auto md:max-w-[440px] p-6' } : {};

  const formRef = useRef<HTMLFormElement | null>(null);

  const Duration = memo(function Duration({ type }: { type: string }) {
    switch (type) {
      case 'hours':
        return (
          <div className="mx-auto mb-5 flex w-[70%] flex-wrap justify-center gap-1 md:w-[55%]">
            {Array.from(Array(12)).map((_, idx) => (
              <RadioButton
                required
                key={`custom_duration_${idx}`}
                name="duration"
                value={idx + 1 + ' hour' + (idx + 1 > 1 ? 's' : '')}
                label={String(idx + 1)}
                variant="secondary"
                className="w-10!"
                defaultChecked={idx === 0}
                inputClassName={styles.radioField}
              />
            ))}
          </div>
        );
      case 'days':
        return (
          <div className="mx-auto mb-5 flex w-[60%] flex-wrap justify-center gap-1 md:w-[90%]">
            {dayInputMode === DAY_INPUT_MODE_RADIO ? (
              Array.from(Array(7)).map((_, idx) => (
                <RadioButton
                  required
                  key={`custom_duration_${idx}`}
                  name="duration"
                  value={idx + 1 + ' day' + (idx + 1 > 1 ? 's' : '')}
                  label={String(idx + 1)}
                  variant="secondary"
                  className="w-10!"
                  defaultChecked={idx === 0}
                  inputClassName={styles.radioField}
                />
              ))
            ) : (
              <Select tabIndex={0} name="duration_select" className="w-[303px] max-w-[303px]">
                {Array.from(Array(21)).map((_, idx) => (
                  <option key={`custom_duration_${idx + 8}`} value={idx + 8 + ' days'}>
                    {idx + 8}
                  </option>
                ))}
              </Select>
            )}
            {daysOnly === false && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (dayInputMode === DAY_INPUT_MODE_RADIO) {
                    setDayInputMode(DAY_INPUT_MODE_DROPDOWN);
                  } else {
                    setDayInputMode(DAY_INPUT_MODE_RADIO);
                  }
                }}
                className={`bg-neutral border-secondary-400 hover:primary-100 flex w-10 items-center justify-center rounded border-1 py-2 text-center text-sm uppercase ${dayInputMode === DAY_INPUT_MODE_RADIO ? 'bg-neutral hover:bg-background-300 text-primary border-secondary-400' : 'bg-secondary text-neutral'}`}
                aria-label="Change Input"
              >
                {dayInputMode === DAY_INPUT_MODE_RADIO ? (
                  <IconChevronDown width={20} height={20} className="text-secondary" />
                ) : (
                  <IconChevronUp width={20} height={20} className="text-neutral" />
                )}
              </button>
            )}
          </div>
        );
      case 'weeks':
        return (
          <div className="mx-auto mb-5 flex w-[80%] flex-wrap justify-center gap-1">
            {Array.from(Array(4)).map((_, idx) => (
              <RadioButton
                defaultChecked={idx === 0}
                key={`custom_duration_${idx}`}
                name="duration"
                value={idx + 1 + ' week' + (idx + 1 > 1 ? 's' : '')}
                label={String(idx + 1)}
                variant="secondary"
                className="w-10!"
                inputClassName={styles.radioField}
              />
            ))}
          </div>
        );
      default:
        return null;
    }
  });

  return (
    <div className="mx-auto md:max-w-[800px]">
      {heading && <h1 className="mx-auto mb-4! text-left md:text-center">{heading}</h1>}
      {subHeading && <div className="tc-subheading mt-0 mb-8 text-left md:text-center">{subHeading}</div>}

      <Wrapper {...wrapperProps}>
        {formIntroText && <p className="text-dark mb-4 tracking-[0.12px]">{formIntroText}</p>}
        <form
          ref={formRef}
          noValidate
          action={process.env.NEXT_PUBLIC_MOTOR_URL}
          data-component-id={LANDING_PAGE_FORM_TEST_ID}
          onSubmit={(e) => {
            if (!accepted && checkboxLabel) {
              setShowError(true);
              e.preventDefault();
              return;
            }

            if (!formRef.current!.checkValidity()) {
              e.stopPropagation();
              e.preventDefault();
            }

            const formData = new FormData(e.target as HTMLFormElement);

            if (formData.has('duration_select')) {
              formRef.current?.querySelector('select[name="duration_select"]')?.setAttribute('name', 'duration');
              formRef.current?.querySelectorAll('input[name="duration"]').forEach((el) => {
                el.setAttribute('disabled', 'true');
              });
            }

            window.dataLayer = window.dataLayer || [];

            // old event for rebrand
            window.dataLayer.push({
              event: 'amplitude_event',
              event_name: 'form_submitted',
              event_properties: {
                journey,
                path: window.location.pathname,
              },
            });

            let source = 'direct';
            if (window?.location?.search && window.location.search.includes('utm_source')) {
              source = new URLSearchParams(window.location.search).get('utm_source')!;
            }

            // new event
            window.dataLayer.push({
              event: 'amplitude_event',
              event_name: 'registration_submitted',
              event_properties: {
                product_type: type,
                registration: reg,
                source: source,
              },
            });
          }}
        >
          {journey != 'none' && <input type="hidden" name={journey} value="true" />}
          <input type="hidden" name="tp" value="23212" />
          <input type="hidden" name="auth-interstitial" value="true" />

          {/* useSearchParams() suspends in Next 15 SSR — isolate it here so only
              these hidden inputs are deferred, not the entire form. */}
          <Suspense>
            <SearchParamInputs />
          </Suspense>

          <div className={`${styles.fieldContainer}`}>
            <CarSelectionForm
              className="mb-4"
              regLabel={regLabel}
              showRegLabel={showRegLabel}
              showRegToggle={showRegToggle}
              journey={journey}
              reg={reg}
              type={type}
              setReg={setReg}
              setType={setType}
            />

            {!regPlateOnly && !useBanner && !hideDuration && showDurationPicker && (
              <>
                {!daysOnly && (
                  <fieldset className="mb-6">
                    <legend className={`tc-label mb-1 pl-px`}>Can we guess how long you need cover for?</legend>
                    <div className="gap-regular flex">
                      <RadioButton
                        defaultChecked={defaultDurationType === FIXED_DURATION_ONE_DAY}
                        name="duration"
                        disabled={false}
                        checked={durationType === FIXED_DURATION_ONE_DAY}
                        onClick={() => setDurationType(FIXED_DURATION_ONE_DAY)}
                        value="1 day"
                        label="1 day"
                        inputClassName={styles.radioField}
                      />
                      <RadioButton
                        defaultChecked={defaultDurationType === FIXED_DURATION_TWO_DAYS}
                        name="duration"
                        disabled={false}
                        checked={durationType === FIXED_DURATION_TWO_DAYS}
                        onClick={() => setDurationType(FIXED_DURATION_TWO_DAYS)}
                        value="2 days"
                        label="2 days"
                        inputClassName={styles.radioField}
                      />
                      <RadioButton
                        defaultChecked={defaultDurationType === FIXED_DURATION_ONE_WEEK}
                        name="duration"
                        disabled={false}
                        checked={durationType === FIXED_DURATION_ONE_WEEK}
                        onClick={() => setDurationType(FIXED_DURATION_ONE_WEEK)}
                        value="1 week"
                        label="1 week"
                        inputClassName={styles.radioField}
                      />
                    </div>
                  </fieldset>
                )}

                <fieldset className="mb-3">
                  <legend className={`tc-label mb-1 pl-px`}>
                    {daysOnly ? 'Choose your duration below' : 'Maybe not, choose your own duration'}
                  </legend>
                  <div className="gap-regular mb-5 flex justify-center">
                    {!!daysOnly === false && (
                      <RadioButton
                        name="duration"
                        disabled={false}
                        required
                        onClick={() => setDurationType('hours')}
                        checked={durationType === 'hours'}
                        label="Hours"
                        variant="secondary"
                      />
                    )}
                    <RadioButton
                      name="duration"
                      disabled={false}
                      required
                      onClick={() => setDurationType('days')}
                      checked={durationType === 'days' || daysOnly}
                      label="Days"
                      variant="secondary"
                    />
                    {!!daysOnly === false && (
                      <RadioButton
                        name="duration"
                        disabled={false}
                        required
                        onClick={() => setDurationType('weeks')}
                        checked={durationType === 'weeks'}
                        label="Weeks"
                        variant="secondary"
                      />
                    )}
                  </div>

                  {daysOnly ? <Duration type="days" /> : <Duration type={durationType} />}

                  <p
                    className={`${styles.validationError} text-error mt-2 mb-1 text-center text-sm`}
                    aria-live="assertive"
                  >
                    Please select how long you need cover for.
                  </p>
                </fieldset>
              </>
            )}

            {!regPlateOnly && !useBanner && !hideDuration && !showDurationPicker && (
              <input type="hidden" name="duration" value={defaultDurationType || FIXED_DURATION_TWO_DAYS} />
            )}
          </div>

          {!regPlateOnly && useBanner && (
            <div className="bg-primary text-neutral mx-auto mt-8 mb-3 flex w-fit items-center gap-2 rounded p-4 align-middle">
              <IconCircleCheckFilled width={40} height={40} className="text-neutral mx-auto" />
              <div className="flex-1 px-10 text-center uppercase">
                <p className="mb-0 text-2xl">{bannerCoverDuration}</p>
                <p className="mt-0">{bannerCoverType}</p>
              </div>
            </div>
          )}

          {checkboxLabel && (
            <div className={`mx-auto my-6 flex w-auto gap-2 p-2 ${showError ? 'border-error text-error border' : ''}`}>
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => {
                  setAccepted(e.target.checked);
                  setShowError(false);
                }}
                required
                value="yes"
              />
              <p className="mb-3 text-center text-lg">{checkboxLabel}</p>
            </div>
          )}

          <button
            type="submit"
            onClick={() => trackCtaClick(submitButtonText, 'form_submit')}
            className={
              submitButtonColor === 'purple' ? 'tc-submit-button tc-submit-button--purple' : 'tc-submit-button'
            }
          >
            {submitButtonText}
          </button>
        </form>
      </Wrapper>
    </div>
  );
};

/**
 * Renders URL search params as hidden form inputs so they are forwarded
 * on form submission. Deferred to client-only so the server always
 * produces the same HTML regardless of which query params are present,
 * preventing hydration mismatches when the CDN serves cached responses.
 */
function SearchParamInputs() {
  const searchParams = useSearchParams();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <>
      {[...searchParams.entries()].map(([k, v], idx) => (
        <input key={k + v + idx} type="hidden" name={k} value={v} />
      ))}
    </>
  );
}

export const LandingPageForm = (props: LandingPageFormProps) => {
  return <LandingPageFormInner {...props} />;
};
